import { z } from "zod";
import { colomboBusinessDate } from "../core";
import type { Device, StaffSecret, User } from "../types";
import { RECOVERY_ITERATIONS, audit, constantTimeEqual, fail, getSettings, hashSecret, parse, raiseAlert, requirePermission, sha256Hex, type Ctx } from "./context";

/*
 * Cloud sign-in goes through the server (apps/api /auth/login), which runs `auth.signInCheck` before it hands out a
 * session. Only such sessions can read the database (firestore.rules) or run commands, so a password alone is not enough:
 *   - with device approval on, the computer or phone must be one the Super Admin approved, and a management account must be
 *     approved on it in person (a Super Admin cannot approve their own device);
 *   - management (Super Admin, manager, viewer) also enters the 6-digit code from an authenticator app (TOTP, RFC 6238);
 *   - cashiers, waiters and the kitchen get a session limited to today's records.
 */

const BASE32 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";
const base32 = (bytes: Uint8Array) => {
  let bits = 0, value = 0, out = "";
  for (const byte of bytes) { value = (value << 8) | byte; bits += 8; while (bits >= 5) { out += BASE32[(value >>> (bits - 5)) & 31]; bits -= 5; } }
  return bits > 0 ? out + BASE32[(value << (5 - bits)) & 31] : out;
};
const fromBase32 = (text: string) => {
  const bytes: number[] = [];
  let bits = 0, value = 0;
  for (const char of text.replace(/[\s=]/g, "").toUpperCase()) {
    const index = BASE32.indexOf(char);
    if (index < 0) continue;
    value = (value << 5) | index; bits += 5;
    if (bits >= 8) { bytes.push((value >>> (bits - 8)) & 255); bits -= 8; }
  }
  return new Uint8Array(bytes);
};

/** The 6-digit code an authenticator app shows for the 30-second step `step`. */
export async function totpCode(secret: string, step: number): Promise<string> {
  const counter = new Uint8Array(8);
  new DataView(counter.buffer).setBigUint64(0, BigInt(step));
  const key = await crypto.subtle.importKey("raw", fromBase32(secret), { name: "HMAC", hash: "SHA-1" }, false, ["sign"]);
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", key, counter));
  const offset = mac[19]! & 15;
  const number = ((mac[offset]! & 127) << 24) | (mac[offset + 1]! << 16) | (mac[offset + 2]! << 8) | mac[offset + 3]!;
  return String(number % 1_000_000).padStart(6, "0");
}
/** The step a code belongs to, allowing one step either side for a phone clock that is slightly off; undefined if wrong. */
export async function totpStep(secret: string, code: string, now: Date): Promise<number | undefined> {
  const clean = code.replace(/\D/g, "");
  if (clean.length !== 6) return undefined;
  const current = Math.floor(now.getTime() / 30_000);
  for (const step of [current, current - 1, current + 1]) if (await totpCode(secret, step) === clean) return step;
  return undefined;
}
export const newTotpSecret = () => base32(crypto.getRandomValues(new Uint8Array(20)));

/** Roles that see the restaurant's figures, and so sign in with an authenticator code as well as the password. */
/** How long a device stays trusted after the authenticator code is entered on it (the code is asked again after). */
export const TRUSTED_DEVICE_DAYS = 30;
export const MFA_ROLES: readonly User["role"][] = ["super_admin", "manager", "viewer", "accountant"];
/** Roles whose session sees only today's records (and still-open bills); management sees the history. */
export const DAY_LIMITED_ROLES: readonly User["role"][] = ["cashier", "waiter", "kitchen"];
/** The device's stored id: a hash of the random id the device keeps, so the raw id never sits in the database. */
export const deviceKey = (deviceId: string) => sha256Hex(`device:${deviceId}`);

export type SignInDecision =
  | { status: "ok"; since: string | null; deviceKey: string; mfaSetupRequired: boolean }
  | { status: "mfa_required" | "device_pending" | "denied"; message: string };

const SignInCheckSchema = z.object({ uid: z.string().min(1).max(128), deviceId: z.string().min(16).max(128), deviceLabel: z.string().trim().max(80).default(""), code: z.string().max(12).default(""),
  /** A Super Admin's printed recovery code, for when they are locked out (a new device, or the authenticator phone lost). */
  recovery: z.string().max(40).default("") });

/**
 * Decides whether a sign-in that already passed the password check may start a session. Server only (system actor).
 * Every sign-in is recorded against its device, so the Super Admin sees each computer and phone that was used.
 */
export async function signInCheck(ctx: Ctx, raw: unknown): Promise<SignInDecision> {
  if (ctx.actor.id !== "system") fail("permission-denied", "Not available.");
  const input = parse(SignInCheckSchema, raw);
  const user = await ctx.tx.get("users", input.uid);
  if (!user || user.disabled) return { status: "denied", message: "This account has been disabled. Ask the Super Admin." };
  const settings = await getSettings(ctx.tx);
  const key = await deviceKey(input.deviceId);
  const stored = await ctx.tx.get("devices", key);
  const secret = await ctx.tx.get("staffSecrets", user.id);

  // The break-glass: a Super Admin locked out (a new device nobody else can approve, or the authenticator phone lost) signs
  // in with the password and the printed recovery code. It approves this device for them, works once, and tells management.
  if (input.recovery) return recoverSignIn(ctx, user, key, stored, input.deviceLabel, input.recovery, secret);

  let device: Device = stored ?? { id: key, label: input.deviceLabel || "Unnamed device", status: "pending", requestedAt: ctx.iso, lastUserId: user.id, lastUserName: user.displayName, lastUsedAt: ctx.iso, schemaVersion: 1 };
  // A device that was in use before device approval was switched on is not a new device: it keeps working for everyone,
  // as devices approved before people were approved one by one do. Only devices first seen after it need approving.
  if (settings.deviceApproval && device.status === "pending" && stored && stored.requestedAt < await approvalSince(ctx, settings)) {
    const { approvedUsers: _none, pendingUsers: _waiting, ...rest } = device;
    device = { ...rest, status: "approved", approvedByName: "In use before device approval", approvedAt: ctx.iso };
    audit(ctx, "device.kept_from_before_approval", "device", device.id, { label: device.label, firstSeen: device.requestedAt });
  }
  // A device approved before people were approved one by one has no list: it stays open to every account, as approved then.
  const legacy = device.status === "approved" && device.approvedUsers === undefined;
  const approvedUsers = device.approvedUsers ?? [];
  const seen: Device = { ...device, label: input.deviceLabel || device.label, lastUserId: user.id, lastUserName: user.displayName, lastUsedAt: ctx.iso };
  const personNeeded = MFA_ROLES.includes(user.role) && !legacy;
  const personApproved = approvedUsers.some((entry) => entry.id === user.id);
  if (settings.deviceApproval && (device.status !== "approved" || (personNeeded && !personApproved))) {
    const pendingUsers = personNeeded && !personApproved && device.status !== "revoked"
      ? [...(device.pendingUsers ?? []).filter((entry) => entry.id !== user.id), { id: user.id, name: user.displayName, at: ctx.iso }] : device.pendingUsers ?? [];
    ctx.tx.set("devices", { ...seen, status: device.status === "revoked" ? "revoked" : device.status === "approved" ? "approved" : "pending", pendingUsers });
    return { status: "device_pending", message: device.status === "revoked"
      ? "This device was blocked by the Super Admin. Sign in from an approved computer."
      : device.status === "approved"
        ? `${user.displayName} is not approved on this device yet. Ask a Super Admin to approve you on it under Staff & Access → Devices. Once approved, sign in again with your password${MFA_ROLES.includes(user.role) && secret?.totpSecret ? " and the code showing then in your authenticator app" : ""}.`
        : `This computer or phone is not approved yet. Ask the Super Admin to approve it under Staff & Access → Devices. Once approved, sign in again with your password${MFA_ROLES.includes(user.role) && secret?.totpSecret ? " and the code showing then in your authenticator app" : ""}.` };
  }

  // Management's second step, asked only once the device is cleared (there is no point typing a code that cannot be used
  // yet). Approval alone never lets anyone in: the code is still needed. A code works once: a used step cannot sign in again.
  // A device where this person entered the code is trusted for TRUSTED_DEVICE_DAYS: there the password is enough. A new
  // device, another person, or an authenticator reset asks for the code again.
  const trusted = (secret?.trustedDevices?.[key] ?? "") > ctx.iso;
  if (MFA_ROLES.includes(user.role) && secret?.totpSecret && !trusted) {
    if (!input.code) return { status: "mfa_required", message: "Enter the 6-digit code from your authenticator app." };
    const step = await totpStep(secret.totpSecret, input.code, ctx.now);
    if (step === undefined || step <= (secret.totpLastStep ?? 0)) return { status: "mfa_required", message: "That code is not right or was already used. Enter the current code from the app." };
    const still = Object.fromEntries(Object.entries(secret.trustedDevices ?? {}).filter(([, until]) => until > ctx.iso));
    ctx.tx.set("staffSecrets", { ...secret, totpLastStep: step, trustedDevices: { ...still, [key]: new Date(ctx.now.getTime() + TRUSTED_DEVICE_DAYS * 86_400_000).toISOString() } });
  }
  ctx.tx.set("devices", settings.deviceApproval ? seen : remembered(seen, user));

  return {
    status: "ok", deviceKey: key,
    since: DAY_LIMITED_ROLES.includes(user.role) ? colomboBusinessDate(ctx.now) : null,
    mfaSetupRequired: MFA_ROLES.includes(user.role) && !secret?.totpSecret
  };
}

async function recoverSignIn(ctx: Ctx, user: User, key: string, stored: Device | undefined, label: string, code: string, secret: StaffSecret | undefined): Promise<SignInDecision> {
  const { hash } = await hashSecret(code.toUpperCase().replace(/[^0-9A-Z]/g, ""), secret?.recoverySalt ?? "AAAAAAAAAAAAAAAAAAAAAA==", secret?.recoveryIterations ?? RECOVERY_ITERATIONS);
  const valid = user.role === "super_admin" && Boolean(secret?.recoveryHash) && constantTimeEqual(hash, secret!.recoveryHash!);
  if (!valid || !secret) {
    audit(ctx, "auth.recovery_failed", "user", user.id, { device: label });
    return { status: "denied", message: "That recovery code is not right, or it was already used. Only a Super Admin's own printed code works." };
  }
  const device: Device = stored ?? { id: key, label: label || "Unnamed device", status: "pending", requestedAt: ctx.iso, lastUserId: user.id, lastUserName: user.displayName, lastUsedAt: ctx.iso, schemaVersion: 1 };
  const people = device.approvedUsers;
  ctx.tx.set("devices", {
    ...device, label: label || device.label, status: "approved", lastUserId: user.id, lastUserName: user.displayName, lastUsedAt: ctx.iso, approvedByName: `${user.displayName} (recovery code)`, approvedAt: ctx.iso,
    // A device open to everyone (approved before per-person approval) stays so; otherwise this Super Admin is added to it.
    ...(people === undefined && device.status === "approved" ? {} : { approvedUsers: [...(people ?? []).filter((entry) => entry.id !== user.id), { id: user.id, name: user.displayName }] }),
    pendingUsers: (device.pendingUsers ?? []).filter((entry) => entry.id !== user.id)
  });
  // The code works once: a new one is made in Settings after signing in.
  const { recoveryHash: _used, recoverySalt: _salt, recoveryIterations: _rounds, ...rest } = secret;
  ctx.tx.set("staffSecrets", rest);
  ctx.tx.set("users", { ...user, hasRecoveryCode: false, updatedAt: ctx.iso });
  audit(ctx, "auth.recovered_with_code", "user", user.id, { device: label || device.label });
  raiseAlert(ctx, "system", "Recovery code used", `${user.displayName} signed in with the Super Admin recovery code on "${label || device.label}". If this was not them, block that device under Staff & Access.`);
  return { status: "ok", deviceKey: key, since: null, mfaSetupRequired: !secret.totpSecret };
}

/**
 * While device approval is off, every device that signs in, and each management person on it, is recorded as approved, so
 * switching approval on later locks out only devices and people that were never used before. A blocked device stays blocked.
 */
function remembered(device: Device, user: User): Device {
  if (device.status === "revoked") return device;
  if (device.status === "approved" && device.approvedUsers === undefined) return device;
  const people = device.approvedUsers ?? [];
  const approvedUsers = MFA_ROLES.includes(user.role) && !people.some((entry) => entry.id === user.id) ? [...people, { id: user.id, name: user.displayName }] : people;
  return { ...device, status: "approved", approvedUsers, pendingUsers: (device.pendingUsers ?? []).filter((entry) => entry.id !== user.id) };
}

/** When device approval was switched on: kept in the settings since this version, and found in the audit log before it. */
async function approvalSince(ctx: Ctx, settings: { deviceApprovalSince?: string }): Promise<string> {
  if (settings.deviceApprovalSince) return settings.deviceApprovalSince;
  const switchedOn = await ctx.tx.query("auditLogs", [{ field: "action", op: "==", value: "device.approval_on" }]);
  return switchedOn.map((log) => log.createdAt).sort().at(-1) ?? "";
}

/** Starts authenticator setup: a new secret the app scans as a QR code. It takes effect only once a code from it is confirmed. */
export async function beginTotp(ctx: Ctx) {
  const user = await ctx.tx.get("users", ctx.actor.id) ?? fail("not-found", "Account not found.");
  const totpPending = newTotpSecret();
  ctx.tx.set("staffSecrets", { ...(await ctx.tx.get("staffSecrets", user.id) ?? { id: user.id }), totpPending });
  const label = encodeURIComponent(`Mango Moons POS:${user.email}`);
  return { secret: totpPending, uri: `otpauth://totp/${label}?secret=${totpPending}&issuer=${encodeURIComponent("Mango Moons POS")}&digits=6&period=30` };
}
export async function confirmTotp(ctx: Ctx, raw: unknown) {
  const { code } = parse(z.object({ code: z.string().max(12) }), raw);
  const user = await ctx.tx.get("users", ctx.actor.id) ?? fail("not-found", "Account not found.");
  const secret = await ctx.tx.get("staffSecrets", user.id);
  if (!secret?.totpPending) fail("failed-precondition", "Start the setup again.");
  const step = await totpStep(secret.totpPending, code, ctx.now);
  if (step === undefined) fail("invalid-argument", "That code is not right. Check the phone's time is correct and enter the current code.");
  const { totpPending: _done, ...rest } = secret;
  ctx.tx.set("staffSecrets", { ...rest, totpSecret: secret.totpPending, totpLastStep: step });
  ctx.tx.set("users", { ...user, hasTotp: true, updatedAt: ctx.iso });
  audit(ctx, "auth.totp_enabled", "user", user.id);
  return { ok: true };
}

/** For a lost phone: the account sets up the authenticator again at its next sign-in. Super Admin only. */
export async function resetTotp(ctx: Ctx, raw: unknown) {
  if (ctx.actor.role !== "super_admin") fail("permission-denied", "Only a Super Admin can reset an authenticator.");
  const { uid } = parse(z.object({ uid: z.string().min(1).max(128) }), raw);
  const user = await ctx.tx.get("users", uid) ?? fail("not-found", "Account not found.");
  const secret = await ctx.tx.get("staffSecrets", uid);
  if (secret) { const { totpSecret: _old, totpPending: _pending, totpLastStep: _step, trustedDevices: _trusted, ...rest } = secret; ctx.tx.set("staffSecrets", rest); }
  ctx.tx.set("users", { ...user, hasTotp: false, updatedAt: ctx.iso });
  audit(ctx, "auth.totp_reset", "user", uid, { name: user.displayName });
  return { uid };
}

/** Approves or blocks a computer or phone. Blocking also returns who used it last, so the server can end that session. */
export async function setDeviceStatus(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "staff.manage");
  const input = parse(z.object({
    deviceId: z.string().min(1).max(128), status: z.enum(["approved", "revoked", "remove_user"]), label: z.string().trim().max(80).optional(),
    /** With "approved": also approves this management account on the device. With "remove_user": takes it off. */
    userId: z.string().min(1).max(128).optional()
  }), raw);
  const device = await ctx.tx.get("devices", input.deviceId) ?? fail("not-found", "Device not found.");
  const approvedUsers = device.approvedUsers ?? [];
  const legacy = device.status === "approved" && device.approvedUsers === undefined;
  const person = input.userId ? await ctx.tx.get("users", input.userId) ?? fail("not-found", "Account not found.") : undefined;
  if (legacy && input.status === "remove_user") fail("failed-precondition", "Everyone can sign in on this device (it was approved before people were approved one by one). Block the device to stop that.");
  if (person && person.id === ctx.actor.id && input.status === "approved") fail("permission-denied", "Another Super Admin must approve you on a device.");
  const pendingUsers = (device.pendingUsers ?? []).filter((entry) => entry.id !== person?.id);
  const people = input.status === "remove_user" ? approvedUsers.filter((entry) => entry.id !== person?.id)
    : person && !approvedUsers.some((entry) => entry.id === person.id) ? [...approvedUsers, { id: person.id, name: person.displayName }] : approvedUsers;
  ctx.tx.set("devices", {
    // A device approved before per-person approval stays open to everyone; approving it again changes nothing there.
    ...device, ...(legacy ? {} : { approvedUsers: people }), pendingUsers, status: input.status === "remove_user" ? device.status : input.status, ...(input.label ? { label: input.label } : {}),
    ...(input.status === "approved" ? { approvedByName: ctx.actor.name, approvedAt: ctx.iso } : {})
  });
  audit(ctx, input.status === "approved" ? "device.approved" : input.status === "revoked" ? "device.revoked" : "device.person_removed", "device", device.id, { label: input.label ?? device.label, person: person?.displayName ?? "" });
  return { lastUserId: input.status === "remove_user" ? person?.id ?? null : device.lastUserId };
}

/** Turns "only approved devices can sign in" on or off. Turning it on approves the device the Super Admin is using. */
export async function setDeviceApproval(ctx: Ctx, raw: unknown) {
  if (ctx.actor.role !== "super_admin") fail("permission-denied", "Only a Super Admin can change this.");
  const input = parse(z.object({ on: z.boolean(), currentDeviceId: z.string().min(16).max(128).optional() }), raw);
  if (input.on) {
    const key = input.currentDeviceId ? await deviceKey(input.currentDeviceId) : undefined;
    const current = key ? await ctx.tx.get("devices", key) : undefined;
    if (!current) fail("failed-precondition", "Sign out and in again on this computer first, so it is listed.");
    // The Super Admin switching it on is approved on the device they are using (others there need approving in person).
    const people = current.approvedUsers ?? (current.status === "approved" ? undefined : [{ id: ctx.actor.id, name: ctx.actor.name }]);
    ctx.tx.set("devices", { ...current, ...(people ? { approvedUsers: people } : {}), status: "approved", approvedByName: ctx.actor.name, approvedAt: ctx.iso });
  }
  const before = await getSettings(ctx.tx);
  ctx.tx.set("settings", { ...before, deviceApproval: input.on, ...(input.on && !before.deviceApproval ? { deviceApprovalSince: ctx.iso } : {}), updatedAt: ctx.iso });
  audit(ctx, input.on ? "device.approval_on" : "device.approval_off", "settings", "restaurant");
  return { ok: true };
}

/** A returning customer by phone, for staff who may take orders but not browse the customer list. */
export async function lookupCustomer(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const { phone } = parse(z.object({ phone: z.string().regex(/^\+?[0-9]{9,15}$/) }), raw);
  const customer = (await ctx.tx.query("customers", [{ field: "phone", op: "==", value: phone }]))[0];
  return customer && customer.active !== false ? { id: customer.id, name: customer.name, points: customer.points, orderCount: customer.orderCount } : null;
}

